import { useId, useRef, useState } from 'react';
import { C } from '@ridgewill/brand';
import { acceptAgreement, type Agreement } from '../lib/api.js';
import { Markdown } from '../lib/markdown.js';
import { ConfirmDialog } from '../sections/CloseAccount.js';
import { Problem, button } from './form.js';

/**
 * Ridgewill's own agreements, owed before the rest of the account opens.
 *
 * ── WHAT IT GATES, AND WHAT IT DELIBERATELY DOES NOT ─────────────────
 *
 * Org-wide agreements only (Jon, 2026-10-10): the Ridgewill Terms and Privacy
 * Policy. An app's own terms are accepted in that app, where they apply, so
 * somebody who came here to change a password is not asked about Keyodo.
 * Nor are agreements owed by one group, such as the Testing Terms owed by
 * testers (auth#612): those govern Test Versions inside apps, and this portal is
 * not one. `gatesAccount` in lib/api.ts is the rule.
 *
 * Three ways out stay open while it is up, also Jon's: contact support, close
 * the account, sign out. Somebody who does not accept new terms has to be able
 * to leave, and closing an account is a privacy right that cannot sit behind
 * agreeing to the document that describes it.
 *
 * ── ONE CHECKBOX PER DOCUMENT ────────────────────────────────────────
 *
 * Jon's choice over a single button. Each box is its own statement about one
 * document, and the full text sits directly above it, so the record is "this
 * person ticked a box under this text" rather than "this person pressed a
 * button near some links". Acceptance is still recorded per document on the
 * server; the boxes only decide when the button may be pressed.
 *
 * ── THE TEXT IS THE TEXT THEY AGREE TO ───────────────────────────────
 *
 * It comes from the version row being accepted (`content`), not from a hosted
 * page that could have changed since. That is the whole reason auth stores it
 * inline and immutable.
 */
export function AgreementGate({
  pending,
  onSignOut,
  onAccepted,
}: {
  /** Offered in the ways out, not only in the header two panes up. */
  onSignOut: () => void;
  /** Already narrowed to org-wide by the caller. Never empty. */
  pending: Agreement[];
  /** Re-read what is owed; the gate disappears when nothing is. */
  onAccepted: () => void;
}) {
  const [checked, setChecked] = useState<Record<string, boolean>>({});
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [closing, setClosing] = useState(false);

  const allChecked = pending.every((a) => checked[a.agreementVersionId]);
  const remaining = pending.filter((a) => !checked[a.agreementVersionId]).length;

  async function agree() {
    setBusy(true);
    setError(null);
    try {
      /**
       * One at a time, in the order shown. If the second fails the first is
       * already recorded, and `onAccepted` re-reads what is still owed, so the
       * gate comes back with only the one that did not go through rather than
       * asking again for something already on record.
       */
      for (const a of pending) await acceptAgreement(a.agreementVersionId);
      // The screen they asked for opens in place of this one; start it at the
      // top rather than wherever the Agree button happened to leave the page.
      window.scrollTo(0, 0);
      onAccepted();
    } catch (e) {
      setError(
        e instanceof Error && e.message
          ? `${e.message} Nothing was lost; press Agree and continue to try again.`
          : 'That could not be recorded. Press Agree and continue to try again.',
      );
      onAccepted();
    } finally {
      setBusy(false);
    }
  }

  return (
    <div>
      <p style={st.lead}>
        {pending.length === 1
          ? `Read the ${pending[0]?.name ?? 'agreement'} below and confirm to keep using your Ridgewill account.`
          : 'Read each document below and confirm to keep using your Ridgewill account.'}
      </p>

      {pending.map((a, n) => (
        <Document
          key={a.agreementVersionId}
          first={n === 0}
          agreement={a}
          checked={Boolean(checked[a.agreementVersionId])}
          onChange={(v) => setChecked((c) => ({ ...c, [a.agreementVersionId]: v }))}
        />
      ))}

      {error && <Problem>{error}</Problem>}

      <div style={st.actions}>
        <button
          type="button"
          className="rw-press"
          disabled={!allChecked || busy}
          onClick={() => void agree()}
          style={primary(!allChecked || busy)}
        >
          {busy ? 'Recording…' : 'Agree and continue'}
        </button>
        {!allChecked && (
          <span style={st.hint} aria-live="polite">
            {remaining === pending.length && pending.length > 1
              ? 'Tick each box to continue.'
              : remaining === 1 && pending.length > 1
                ? 'One box left to tick.'
                : 'Tick the box to continue.'}
          </span>
        )}
      </div>

      <section style={st.out} aria-labelledby="gate-out">
        <h2 id="gate-out" style={st.outHeading}>
          Don&rsquo;t want to agree?
        </h2>
        <p style={st.outText}>
          The rest of your account stays closed until you do. You can still{' '}
          <a href="/support" style={st.link}>
            contact support
          </a>{' '}
          with a question,{' '}
          <button type="button" onClick={onSignOut} style={{ ...st.linkButton, color: C.brand }}>
            sign out
          </button>
          , or{' '}
          <button type="button" onClick={() => setClosing(true)} style={st.linkButton}>
            delete your account
          </button>
          .
        </p>
      </section>

      <ConfirmDialog open={closing} onClose={() => setClosing(false)} />
    </div>
  );
}

function Document({
  agreement: a,
  first,
  checked,
  onChange,
}: {
  /** The lead sentence already separates the first; no rule above it. */
  first: boolean;
  agreement: Agreement;
  checked: boolean;
  onChange: (v: boolean) => void;
}) {
  const id = useId();
  const text = useRef<HTMLDivElement>(null);
  /**
   * A privacy policy is a notice, not a contract: somebody reads it, they do
   * not agree to it. "I have read" is the honest statement, so do not "fix"
   * this into "agree" to match the Terms row.
   */
  const privacy = /privacy/i.test(a.agreementSlug);
  return (
    <section style={first ? undefined : st.doc} aria-labelledby={`${id}-name`}>
      <div style={st.docHead}>
        <h2 id={`${id}-name`} style={st.docName}>
          {a.name}
        </h2>
        <span style={st.meta}>
          <span style={st.version}>Version {a.version}</span>
          <button
            type="button"
            onClick={() => download(a, text.current)}
            style={st.download}
            aria-label={`Download the ${a.name}`}
          >
            Download
          </button>
        </span>
      </div>

      {/*
        The release summary is markdown like the document itself: a lead line,
        then "- " bullets. Printed as one paragraph it ran the bullets together
        ("age. -A guardian…"), so it goes through the same renderer.
      */}
      {a.summary && (
        <div style={st.summary} className="rw-agreement-summary">
          <strong style={{ color: C.text, display: 'block', marginBottom: '.35rem' }}>What&rsquo;s changed</strong>
          <Markdown source={a.summary} />
        </div>
      )}

      {/*
        Focusable so a keyboard can scroll it: a scroll region nobody can tab
        into is a document a keyboard user is asked to agree to without being
        able to read past the first screen.
      */}
      <div
        ref={text}
        className="rw-agreement"
        tabIndex={0}
        role="region"
        aria-label={`${a.name}, full text`}
        style={st.well}
      >
        {a.contentFormat === 'markdown' ? (
          <Markdown source={a.content} />
        ) : (
          <p style={{ margin: 0, whiteSpace: 'pre-wrap', lineHeight: 1.6 }}>{a.content}</p>
        )}
      </div>

      <label htmlFor={`${id}-ok`} style={{ ...st.check, ...(checked ? st.checkOn : null) }}>
        <input
          id={`${id}-ok`}
          type="checkbox"
          checked={checked}
          onChange={(e) => onChange(e.target.checked)}
          className="rw-check"
        />
        <span>
          {privacy ? (
            <>
              I have read the <strong>{a.name}</strong>.
            </>
          ) : (
            <>
              I have read and agree to the <strong>{a.name}</strong>.
            </>
          )}
        </span>
      </label>
    </section>
  );
}

/**
 * Save the document as a standalone HTML file (Jon, 2026-10-10).
 *
 * ── HTML, NOT PDF ────────────────────────────────────────────────────
 *
 * A PDF made in the browser needs a library several hundred kilobytes
 * large, in a portal that otherwise ships almost none. An HTML file opens in
 * any browser, prints, and saves to PDF from the print dialog, so it reaches the
 * same place without the weight.
 *
 * ── THE PANE'S OWN MARKUP, SO IT IS THE SAME TEXT ────────────────────
 *
 * The file is built from what React already rendered into the reading pane,
 * not from a second rendering. Nothing in that markup came from an HTML
 * string (see lib/markdown), so copying it out cannot carry anything the page
 * itself did not show. The styles inside it are `var(--rw-x, <light value>)`,
 * and with no stylesheet in the file every one falls back to the light value,
 * which is the right theme for paper.
 */
function download(a: Agreement, pane: HTMLDivElement | null) {
  if (!pane) return;
  const esc = (v: string) => v.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
  const saved = new Date().toISOString().slice(0, 10);
  const html = `<!doctype html>
<html lang="en"><head><meta charset="utf-8">
<meta name="viewport" content="width=device-width, initial-scale=1">
<title>${esc(a.name)} (version ${esc(a.version)})</title>
<style>body{max-width:44rem;margin:2.5rem auto;padding:0 1.25rem;font-family:'Public Sans',-apple-system,system-ui,sans-serif;color:#101626;line-height:1.6}h1{font-size:1.6rem;margin:0 0 .25rem}.meta{color:#3a4456;font-size:.9rem;margin:0 0 2rem}</style>
</head><body>
<h1>${esc(a.name)}</h1>
<p class="meta">Ridgewill LLC. Version ${esc(a.version)}. Saved ${saved}.</p>
${pane.innerHTML}
</body></html>
`;
  const url = URL.createObjectURL(new Blob([html], { type: 'text/html' }));
  const link = document.createElement('a');
  link.href = url;
  link.download = `${a.agreementSlug}-${a.version}.html`;
  link.click();
  // After the click has handed the file to the browser, not before.
  setTimeout(() => URL.revokeObjectURL(url), 0);
}

const primary = (disabled: boolean): React.CSSProperties => ({
  ...button,
  marginTop: 0,
  fontWeight: 600,
  padding: '.7rem 1.25rem',
  background: disabled ? 'transparent' : C.brand,
  color: disabled ? C.textMid : '#fff',
  borderColor: disabled ? C.inputBorder : C.brand,
  cursor: disabled ? 'default' : 'pointer',
});

const st = {
  lead: { margin: '0 0 1.25rem', lineHeight: 1.55, color: C.textMid, maxWidth: '60ch' },
  doc: { paddingTop: '1.5rem', marginTop: '1.5rem', borderTop: `1px solid ${C.border}` },
  docHead: {
    display: 'flex',
    alignItems: 'baseline',
    justifyContent: 'space-between',
    gap: '.25rem 1rem',
    flexWrap: 'wrap',
    marginBottom: '.6rem',
  },
  docName: { fontSize: '1.1rem', fontWeight: 700, margin: 0, lineHeight: 1.3 },
  meta: { display: 'inline-flex', alignItems: 'baseline', gap: '.85rem', flexWrap: 'wrap' },
  version: { color: C.textMid, fontSize: '.85rem', fontVariantNumeric: 'tabular-nums' },
  download: {
    background: 'none',
    border: 0,
    padding: 0,
    font: 'inherit',
    fontSize: '.85rem',
    color: C.brand,
    textDecoration: 'underline',
    textUnderlineOffset: '.15em',
    cursor: 'pointer',
  },
  summary: { margin: '0 0 .9rem', lineHeight: 1.55, color: C.textMid, fontSize: '.95rem', maxWidth: '65ch' },
  /* Height and padding live in styles.css (.rw-agreement): they change at
     phone width, and an inline style cannot hold a media query. */
  well: {
    overflowY: 'auto',
    background: C.page,
    border: `1px solid ${C.border}`,
    borderRadius: 6,
    fontSize: '.92rem',
    color: C.text,
    overscrollBehavior: 'contain',
  },
  check: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: '.7rem',
    marginTop: '.85rem',
    padding: '.75rem .9rem',
    border: `1px solid ${C.inputBorder}`,
    borderRadius: 6,
    cursor: 'pointer',
    lineHeight: 1.45,
    transition: 'background-color .15s ease-out, border-color .15s ease-out',
  },
  checkOn: { background: C.blueTint, borderColor: C.brand },
  actions: {
    display: 'flex',
    alignItems: 'center',
    gap: '.6rem 1rem',
    flexWrap: 'wrap',
    marginTop: '1.75rem',
  },
  hint: { color: C.textMid, fontSize: '.9rem' },
  out: { marginTop: '2.25rem', paddingTop: '1.25rem', borderTop: `1px solid ${C.border}` },
  outHeading: { fontSize: '.95rem', fontWeight: 600, margin: '0 0 .35rem' },
  outText: { margin: 0, color: C.textMid, fontSize: '.9rem', lineHeight: 1.55, maxWidth: '65ch' },
  link: { color: C.brand, textUnderlineOffset: '.15em' },
  linkButton: {
    background: 'none',
    border: 0,
    padding: 0,
    font: 'inherit',
    color: C.dangerText,
    textDecoration: 'underline',
    textUnderlineOffset: '.15em',
    cursor: 'pointer',
  },
} satisfies Record<string, React.CSSProperties>;
